import React, {ReactNode, useState, useEffect} from "react"
import {RouteComponentProps, withRouter} from "react-router-dom"

import authentication from "@irati/auth"
import {Team, User} from "@irati/types"
import {UsersForBusinessVariables} from "@irati/identity/apollo/query/UsersForBusiness"
import adminTracking from "@irati-tracking/admin"
import {useIntl} from "@irati/utils/lang"
import History from "@irati/utils/history"
import {useLazyUsersForBusiness} from "@irati/identity/hooks"
import Loading from "@irati/ui/src/Loading"

import AlphabetSearch, {onAlphabet} from "../AlphabetSearch/AlphabetSearch"
import SearchAndSort, {onSearch, onSort, onFilter} from "../SearchAndSort"
import NoUsers from "../NoUsers/NoUsers"
import {A_Z} from "../../constants/sort"
import {FILTER_OPTIONS_NO_DEACTIVATED, FILTER_OPTIONS_WITH_MANAGERS} from "../../constants/filter"
import SearchAndSortBusinessUnit from "../SearchAndSortBusinessUnit"
import NoManagers from "../NoManagers"
import FilterEsdAndDob from "../FilterEsdAndDob"
import ShowMore from "./ShowMore"

export const INITIAL_USERLIST_VARIABLES = (
  teamId: string | null,
  hasEmploymentStartDate?: boolean,
  hasDateOfBirth?: boolean
): UsersForBusinessVariables => ({
  includeBusiness: false,
  hasEmploymentStartDate,
  hasDateOfBirth,
  enabled: true,
  limit: 10,
  offset: 0,
  businessId: authentication.business().uuid,
  teamId,
  sort: A_Z,
  includeTeams: true,
  cursor: null
})

interface Props extends RouteComponentProps<{}, {}, {toggleDatesFilter?: boolean}> {
  teamId: string | null
  protectedTeams: Team[]
  businessUnits?: Team[]
  filterOptions?: any[]
  refetchOnMount: boolean
  children: (props: {users: User[]; variables: UsersForBusinessVariables}) => ReactNode
  filterToggle: boolean
  onUpdateVariables?: (variables: UsersForBusinessVariables) => void
}

const UsersList = ({
  teamId,
  protectedTeams,
  businessUnits,
  filterOptions = FILTER_OPTIONS_NO_DEACTIVATED,
  filterToggle,
  refetchOnMount = false,
  onUpdateVariables,
  children,
  ...props
}: Props) => {
  const [variables, setVariables] = useState<UsersForBusinessVariables>(
    INITIAL_USERLIST_VARIABLES(
      teamId,
      props?.location?.state?.toggleDatesFilter ? false : undefined,
      props?.location?.state?.toggleDatesFilter ? false : undefined
    )
  )
  const [checkEsd, setCheckEsd] = useState<boolean>(props?.location?.state?.toggleDatesFilter || false)
  const [checkDob, setCheckDob] = useState<boolean>(props?.location?.state?.toggleDatesFilter || false)
  const [searchAfter, setSearchAfter] = useState(null)
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState<number>(0)
  const [fetchUsers, {data, load}] = useLazyUsersForBusiness(variables, refetchOnMount ? {fetchPolicy: "network-only"} : {})
  const {formatMessage} = useIntl()

  const message = teamId && !variables?.searchString && !variables?.lastNameCharacter && "admin.containers.teamUserList.noUsers.message"
  const businessUnit = businessUnits?.find((t) => t.uuid === teamId)
  const viewingText =
    users.length > 0 &&
    total > 0 &&
    formatMessage({id: "admin.containers.teamUserList.viewingText"}, {currentUsersInView: users.length, totalUsers: total})

  useEffect(() => {
    if (props?.location?.state?.toggleDatesFilter) {
      History.replace({pathname: location.pathname, state: {}})
    }
  }, [])

  useEffect(() => {
    if (variables) {
      handleUpdateVariables(variables)
      fetchUsers()
    }
  }, [variables])

  useEffect(() => {
    if (data) {
      if (variables.cursor === null) {
        setUsers(data.results)
      } else {
        setUsers([...users, ...data.results])
      }
      setSearchAfter(data.cursor)
      setTotal(data.total)
    }
  }, [data, variables.cursor])

  const handleUpdateVariables = (updatedVariables: UsersForBusinessVariables) => {
    if (onUpdateVariables) {
      onUpdateVariables(updatedVariables)
    }
  }

  const handleNextPage = (isVisible: boolean) => {
    if (isVisible && searchAfter && users.length < total) {
      setVariables({...variables, cursor: searchAfter})
    }
  }

  const handleSearch: onSearch = (searchString) => {
    adminTracking.searchPeople()
    setVariables({...variables, searchString, cursor: null})
  }

  const handleFilter: onFilter = (filter) => {
    setVariables({
      ...INITIAL_USERLIST_VARIABLES(teamId, variables?.hasEmploymentStartDate!, variables?.hasDateOfBirth!),
      ...filter,
      searchString: variables?.searchString
    })
  }

  const handleSort: onSort = (sort) => {
    setVariables({...variables, sort, offset: 0, cursor: null})
  }

  const handleBusinessUnit = (businessUnitId: string) => {
    setVariables({
      ...INITIAL_USERLIST_VARIABLES(businessUnitId, variables?.hasEmploymentStartDate!, variables?.hasDateOfBirth!),
      searchString: variables?.searchString
    })
  }

  const handleAlphabet: onAlphabet = (lastNameCharacter) => {
    setVariables({
      ...INITIAL_USERLIST_VARIABLES(teamId, variables?.hasEmploymentStartDate!, variables?.hasDateOfBirth!),
      lastNameCharacter
    })
  }

  const handleChangeCheckEsd = () => {
    setVariables({
      ...variables,
      hasEmploymentStartDate: variables.hasEmploymentStartDate === undefined ? false : undefined,
      cursor: null
    })
    setCheckEsd(!checkEsd)
  }

  const handleChangeCheckDob = () => {
    setVariables({
      ...variables,
      hasDateOfBirth: variables.hasDateOfBirth === undefined ? false : undefined,
      cursor: null
    })
    setCheckDob(!checkDob)
  }

  return (
    <>
      {businessUnits?.length ? (
        <SearchAndSortBusinessUnit
          businessUnits={businessUnits}
          businessUnitId={businessUnit?.uuid}
          filterOptions={filterOptions}
          variables={variables}
          onSearch={handleSearch}
          onFilter={handleFilter}
          onBusinessUnitUpdate={handleBusinessUnit}
          onSort={handleSort}
          viewingText={viewingText}
          filterToggle={filterToggle}
          protectedTeams={protectedTeams}>
          <FilterEsdAndDob checkEsd={checkEsd} checkDob={checkDob} onEsdChange={handleChangeCheckEsd} onDobChange={handleChangeCheckDob} />
        </SearchAndSortBusinessUnit>
      ) : (
        <SearchAndSort
          filterOptions={teamId ? FILTER_OPTIONS_WITH_MANAGERS : filterOptions}
          variables={variables}
          onSearch={handleSearch}
          onFilter={handleFilter}
          onSort={handleSort}
          viewingText={viewingText}
          filterToggle={filterToggle}
          protectedTeams={protectedTeams}>
          <FilterEsdAndDob checkEsd={checkEsd} checkDob={checkDob} onEsdChange={handleChangeCheckEsd} onDobChange={handleChangeCheckDob} />
        </SearchAndSort>
      )}
      <AlphabetSearch onSearch={handleAlphabet} variables={variables} />
      <Loading load={variables.cursor === null ? load : null}>
        {users?.length === 0 && variables.managersOnly && <NoManagers />}
        {users?.length === 0 && !variables.managersOnly && <NoUsers message={message} searchTerm={variables.searchString} />}
        {users?.length > 0 && (
          <>
            {children({variables, users})}
            <ShowMore onNewPage={handleNextPage} />
          </>
        )}
      </Loading>
    </>
  )
}

export default withRouter(UsersList)
